import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "@altimateai/lego";
import { Tabs, TabsList, TabsTrigger } from "@altimateai/lego";
import {
  getTestCases,
  getEvalResults,
  getCriterions,
  TestCase,
  EvalResult,
  Criterion,
} from "../services/evals";

import {
  TestCasesTab,
  CriteriaTab,
  RunsTab,
  ResultsTab,
  PlaygroundAgentsTab,
  RunEvaluationTab,
} from "./components";
import { EvalsHeader } from "./components/EvalsHeader";
import { EvalsModals } from "./components/EvalsModals";
import { useEvalMutations, useUrlState, useTestCaseFilters, useBatchRunState } from "./hooks";
import { useInternalContext } from "../context/InternalContext";

export const Evals: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentTenant } = useInternalContext();
  const [selectedResult, setSelectedResult] = useState<EvalResult | null>(null);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRunNameModal, setShowRunNameModal] = useState(false);
  const [runningTestId, setRunningTestId] = useState<string | null>(null);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandedBatchRun, setExpandedBatchRun] = useState<string | null>(null);
  const [expandedResultId, setExpandedResultId] = useState<string | null>(null);
  const [expandedBatchRunResultId, setExpandedBatchRunResultId] = useState<string | null>(null);
  const [expandedTestCaseId, setExpandedTestCaseId] = useState<string | null>(null);
  const [expandedCriterionId, setExpandedCriterionId] = useState<string | null>(null);
  const [showCreateCriterionModal, setShowCreateCriterionModal] = useState(false);
  const [selectedCriterion, setSelectedCriterion] = useState<Criterion | null>(null);
  const [serverPage, setServerPage] = useState(1);
  const SERVER_PAGE_SIZE = 1000;

  const { data: testCases, isLoading: testCasesLoading } = useQuery({
    queryKey: ["testCases"],
    queryFn: getTestCases,
  });

  const { data: criteria, isLoading: criteriaLoading } = useQuery({
    queryKey: ["criteria"],
    queryFn: getCriterions,
  });

  const currentTab = searchParams.get("tab") || "test-cases";
  const handleTabChange = (tab: string) => navigate(`?tab=${tab}`, { replace: true });

  const {
    createMutation,
    updateMutation,
    deleteMutation,
    deleteResultMutation,
    runEvalMutation,
    createCriterionMutation,
    updateCriterionMutation,
    deleteCriterionMutation,
  } = useEvalMutations();

  const handleRunEval = (testCaseId: string) => {
    setRunningTestId(testCaseId);
    runEvalMutation.mutate(
      { test_case_id: testCaseId },
      {
        onSuccess: () => {
          setRunningTestId(null);
        },
        onError: () => {
          setRunningTestId(null);
        },
      }
    );
    toast({ description: "Run started successfully" });
  };

  const testCasesList = Array.isArray(testCases) ? testCases : [];
  const filteredTestCases =
    selectedTags.length > 0
      ? testCasesList.filter(tc => tc.tags?.some(tag => selectedTags.includes(tag)))
      : testCasesList;

  const { allTags, handleToggleTestCase, handleToggleTag, handleSelectAll, handleDeselectAll } =
    useTestCaseFilters({
      testCases,
      selectedTags,
      filteredTestCases,
      selectedTestCaseIds,
      setSelectedTestCaseIds,
      setSelectedTags,
    });

  const { activeRun, handleBatchRun } = useBatchRunState(
    testCases,
    selectedTestCaseIds,
    setSelectedTestCaseIds,
    handleTabChange
  );

  // Fetch eval results with server-side pagination
  const { data: paginatedResults, isLoading: resultsLoading } = useQuery({
    queryKey: ["evalResults", serverPage],
    queryFn: () => getEvalResults(undefined, serverPage, SERVER_PAGE_SIZE),
    refetchInterval: activeRun ? 10000 : false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const evalResults = paginatedResults?.results;
  const resultsPagination = useMemo(() => {
    if (!paginatedResults) return null;
    return {
      total: paginatedResults.total,
      page: paginatedResults.page,
      pageSize: paginatedResults.page_size,
      totalPages: paginatedResults.total_pages,
      hasNext: paginatedResults.has_next,
      hasPrev: paginatedResults.has_prev,
    };
  }, [paginatedResults]);

  // Handle URL state after all data is loaded
  useUrlState({
    searchParams,
    currentTab,
    evalResults,
    testCases,
    criteria,
    showCreateModal,
    setSelectedResult,
    setExpandedResultId,
    setSelectedTestCase,
    setExpandedTestCaseId,
    setExpandedCriterionId,
    setExpandedBatchRun,
    setExpandedBatchRunResultId,
  });

  return (
    <div className="al-p-6 al-max-w-7xl al-mx-auto al-overflow-y-auto al-overflow-x-hidden">
      <EvalsHeader
        selectedCount={selectedTestCaseIds.length}
        onRunSelected={() => setShowRunNameModal(true)}
        onCreateTestCase={() => setShowCreateModal(true)}
      />

      <Tabs value={currentTab} onValueChange={handleTabChange} className="al-w-full">
        <TabsList className="al-mb-6">
          <TabsTrigger value="test-cases">Test Cases</TabsTrigger>
          <TabsTrigger value="criteria">Criteria</TabsTrigger>
          <TabsTrigger value="run-evaluation">Run Evaluation</TabsTrigger>
          <TabsTrigger value="runs">Batch Runs</TabsTrigger>
          <TabsTrigger value="results">All Results</TabsTrigger>
          <TabsTrigger value="playground-agents">Playground Agents</TabsTrigger>
        </TabsList>

        <TestCasesTab
          testCases={testCases}
          testCasesLoading={testCasesLoading}
          allTags={allTags}
          filteredTestCases={filteredTestCases}
          selectedTestCaseIds={selectedTestCaseIds}
          selectedTags={selectedTags}
          currentTenant={currentTenant}
          runningTestId={runningTestId}
          handleSelectAll={handleSelectAll}
          handleDeselectAll={handleDeselectAll}
          handleToggleTag={handleToggleTag}
          setSelectedTags={setSelectedTags}
          handleToggleTestCase={handleToggleTestCase}
          handleRunEval={handleRunEval}
          deleteMutation={deleteMutation}
          updateMutation={updateMutation}
          expandedTestCaseId={expandedTestCaseId}
          setExpandedTestCaseId={setExpandedTestCaseId}
          navigate={navigate}
          criteria={(Array.isArray(criteria) ? criteria : []).filter(
            (c): c is Criterion =>
              typeof c.id === "string" &&
              typeof c.name === "string" &&
              typeof c.description === "string"
          )}
        />

        <CriteriaTab
          criteria={criteria}
          criteriaLoading={criteriaLoading}
          currentTenant={currentTenant}
          setShowCreateCriterionModal={setShowCreateCriterionModal}
          handleDeleteCriterion={(id: string) => {
            if (window.confirm("Are you sure you want to delete this criterion?"))
              deleteCriterionMutation.mutate(id);
          }}
          updateCriterionMutation={updateCriterionMutation}
          expandedCriterionId={expandedCriterionId}
          setExpandedCriterionId={setExpandedCriterionId}
          navigate={navigate}
        />

        <RunEvaluationTab />

        <RunsTab
          resultsLoading={resultsLoading}
          activeRun={activeRun}
          evalResults={evalResults}
          expandedBatchRun={expandedBatchRun}
          expandedBatchRunResultId={expandedBatchRunResultId}
          setExpandedBatchRun={setExpandedBatchRun}
          setExpandedBatchRunResultId={setExpandedBatchRunResultId}
          setSelectedResult={setSelectedResult}
          navigate={navigate}
          resultsPagination={resultsPagination}
          setServerPage={setServerPage}
        />

        <ResultsTab
          resultsLoading={resultsLoading}
          evalResults={evalResults}
          expandedResultId={expandedResultId}
          setExpandedResultId={setExpandedResultId}
          deleteResultMutation={deleteResultMutation}
          navigate={navigate}
          resultsPagination={resultsPagination}
          setServerPage={setServerPage}
        />

        <PlaygroundAgentsTab
          onRefresh={() => {
            queryClient.invalidateQueries({ queryKey: ["playgroundAgents"] });
            toast({ description: "Agents refreshed successfully" });
          }}
        />
      </Tabs>

      <EvalsModals
        currentTab={currentTab}
        selectedTestCase={selectedTestCase}
        setSelectedTestCase={setSelectedTestCase}
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        selectedResult={selectedResult}
        setSelectedResult={setSelectedResult}
        showRunNameModal={showRunNameModal}
        setShowRunNameModal={setShowRunNameModal}
        showCreateCriterionModal={showCreateCriterionModal}
        setShowCreateCriterionModal={setShowCreateCriterionModal}
        selectedCriterion={selectedCriterion}
        setSelectedCriterion={setSelectedCriterion}
        selectedTestCaseIds={selectedTestCaseIds}
        criteria={criteria}
        handleRunEval={handleRunEval}
        handleBatchRun={handleBatchRun}
        createMutation={createMutation}
        updateMutation={updateMutation}
        deleteMutation={deleteMutation}
        deleteResultMutation={deleteResultMutation}
        createCriterionMutation={createCriterionMutation}
        updateCriterionMutation={updateCriterionMutation}
      />
    </div>
  );
};
